// tslint:disable
import { BaseAvroRecord } from "../../BaseAvroRecord";
import { Origin } from "./OriginEnum";
import { NavigationType } from "./NavigationTypeEnum";

export interface TraceContext {
    traceId: string;
    pageTraceId: string;
    spanId: string;
    parentId: string;
}

export interface Meta {
    uuid: string;
    emitted: number;
    origin: Origin;
    trace?: null | TraceContext;
}

export interface User {
    visitorCookieId?: null | string;
    userId?: null | number;
    subscriptionId?: null | number;
    organizationId?: null | number;
}

export interface Browser {
    userAgent: string;
    language: string;
    platform: string;
    name?: null | string;
}

export interface Tab {
    isNewTab: boolean;
    tabCount: number;
    tabId: string;
}

export interface Navigation {
    referrerUrl: string;
    navigationType?: null | NavigationType;
}

export interface Device {
    browser?: null | Browser;
    tab?: null | Tab;
    navigation: Navigation;
}

export interface Urls {
    currentUrl: string;
    referrerUrl?: null | string;
}

export interface UtmParameters {
    source: string;
    medium: string;
    campaign: string;
    version?: null | number;
    content: string;
    vertical: string;
    language: string;
    mechanism: string;
    offer: string;
    campaign_date?: null | number;
    google_campaign_name: string;
}

export interface SearchIdentifiers {
    searchId: string;
    searchPageId: string;
    lastSearchId?: null | string;
}

export interface Geo {
    countryCode: string;
    country: string;
    continent: string;
}

export interface PageViewEventInterface {
    meta: Meta;
    user: User;
    device: Device;
    urls: Urls;
    utmParameters: UtmParameters;
    searchIdentifiers?: null | SearchIdentifiers;
    clickOrigin: string;
    statusCode: number;
    ipAddress: string;
    geo?: null | Geo;
}

export class PageViewEvent extends BaseAvroRecord implements PageViewEventInterface {

    public meta!: Meta;
    public user!: User;
    public device!: Device;
    public urls!: Urls;
    public utmParameters!: UtmParameters;
    public searchIdentifiers?: null | SearchIdentifiers = null;
    public clickOrigin: string = "";
    public statusCode!: number;
    public ipAddress: string = "";
    public geo?: null | Geo = null;

    public static readonly subject: string = "page-view-event";
    public static readonly schema: object = {
    "type": "record",
    "name": "PageViewEvent",
    "namespace": "storyblocks.navigation",
    "fields": [
        {
            "name": "meta",
            "type": {
                "type": "record",
                "name": "Meta",
                "namespace": "storyblocks.common",
                "fields": [
                    {
                        "name": "uuid",
                        "type": "string"
                    },
                    {
                        "name": "emitted",
                        "type": {
                            "type": "long",
                            "logicalType": "timestamp-millis"
                        }
                    },
                    {
                        "name": "origin",
                        "type": {
                            "type": "enum",
                            "name": "Origin",
                            "symbols": [
                                "VIDEOBLOCKS",
                                "AUDIOBLOCKS",
                                "GRAPHICSTOCK",
                                "STORYBLOCKS",
                                "STORYBLOCKS_NEXTJS",
                                "CONTRIBUTOR_PORTAL",
                                "TEMPLATE_EDITOR",
                                "ACCOUNT_SERVICE",
                                "BILLING_SERVICE",
                                "CONTENT_SERVICE",
                                "PAYOUT_SERVICE",
                                "TRANSLATION_SERVICE",
                                "CONTENT_RATING_APP",
                                "FEATURE_FLAG_SERVICE",
                                "PREMIERE_PRO_PLUGIN",
                                "AFTER_EFFECTS_PLUGIN",
                                "EXAMPLE_SERVICE",
                                "SEARCH_SERVICE",
                                "CONTENT_JOB",
                                "DATA_JOB",
                                "MISC",
                                "STORYBOARD",
                                "OUTDATED_SCHEMA"
                            ],
                            "default": "OUTDATED_SCHEMA"
                        }
                    },
                    {
                        "name": "trace",
                        "type": [
                            "null",
                            {
                                "type": "record",
                                "name": "TraceContext",
                                "fields": [
                                    {
                                        "name": "traceId",
                                        "type": "string"
                                    },
                                    {
                                        "name": "pageTraceId",
                                        "type": "string",
                                        "default": ""
                                    },
                                    {
                                        "name": "spanId",
                                        "type": "string"
                                    },
                                    {
                                        "name": "parentId",
                                        "type": "string",
                                        "default": ""
                                    }
                                ]
                            }
                        ],
                        "default": null
                    }
                ]
            }
        },
        {
            "name": "user",
            "type": {
                "type": "record",
                "name": "User",
                "namespace": "storyblocks.common",
                "fields": [
                    {
                        "name": "visitorCookieId",
                        "type": [
                            "null",
                            "string"
                        ]
                    },
                    {
                        "name": "userId",
                        "type": [
                            "null",
                            "int"
                        ]
                    },
                    {
                        "name": "subscriptionId",
                        "type": [
                            "null",
                            "int"
                        ]
                    },
                    {
                        "name": "organizationId",
                        "type": [
                            "null",
                            "int"
                        ],
                        "default": null
                    }
                ]
            }
        },
        {
            "name": "device",
            "type": {
                "type": "record",
                "name": "Device",
                "namespace": "storyblocks.common",
                "fields": [
                    {
                        "name": "browser",
                        "type": [
                            "null",
                            {
                                "type": "record",
                                "name": "Browser",
                                "namespace": "storyblocks.common.frontend",
                                "fields": [
                                    {
                                        "name": "userAgent",
                                        "type": "string"
                                    },
                                    {
                                        "name": "language",
                                        "type": "string"
                                    },
                                    {
                                        "name": "platform",
                                        "type": "string"
                                    },
                                    {
                                        "name": "name",
                                        "type": [
                                            "null",
                                            "string"
                                        ],
                                        "default": null
                                    }
                                ]
                            }
                        ],
                        "default": null
                    },
                    {
                        "name": "tab",
                        "type": [
                            "null",
                            {
                                "type": "record",
                                "name": "Tab",
                                "namespace": "storyblocks.common.frontend",
                                "fields": [
                                    {
                                        "name": "isNewTab",
                                        "type": "boolean"
                                    },
                                    {
                                        "name": "tabCount",
                                        "type": "int"
                                    },
                                    {
                                        "name": "tabId",
                                        "type": "string"
                                    }
                                ]
                            }
                        ],
                        "default": null
                    },
                    {
                        "name": "navigation",
                        "type": {
                            "type": "record",
                            "name": "Navigation",
                            "namespace": "storyblocks.common.frontend",
                            "fields": [
                                {
                                    "name": "referrerUrl",
                                    "type": "string"
                                },
                                {
                                    "name": "navigationType",
                                    "type": [
                                        "null",
                                        {
                                            "type": "enum",
                                            "name": "NavigationType",
                                            "namespace": "storyblocks.common.enums",
                                            "symbols": [
                                                "TYPE_NAVIGATE",
                                                "TYPE_RELOAD",
                                                "TYPE_BACK_FORWARD",
                                                "TYPE_RESERVED",
                                                "OUTDATED_SCHEMA"
                                            ],
                                            "default": "OUTDATED_SCHEMA"
                                        }
                                    ],
                                    "default": null
                                }
                            ]
                        }
                    }
                ]
            }
        },
        {
            "name": "urls",
            "type": {
                "type": "record",
                "name": "Urls",
                "namespace": "storyblocks.common",
                "fields": [
                    {
                        "name": "currentUrl",
                        "type": "string"
                    },
                    {
                        "name": "referrerUrl",
                        "type": [
                            "null",
                            "string"
                        ],
                        "default": null
                    }
                ]
            }
        },
        {
            "name": "utmParameters",
            "type": {
                "type": "record",
                "name": "UtmParameters",
                "namespace": "storyblocks.common.frontend",
                "fields": [
                    {
                        "name": "source",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "medium",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "campaign",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "version",
                        "type": [
                            "null",
                            "int"
                        ],
                        "default": null
                    },
                    {
                        "name": "content",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "vertical",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "language",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "mechanism",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "offer",
                        "type": "string",
                        "default": ""
                    },
                    {
                        "name": "campaign_date",
                        "type": [
                            "null",
                            {
                                "type": "long",
                                "logicalType": "timestamp-millis"
                            }
                        ],
                        "default": null
                    },
                    {
                        "name": "google_campaign_name",
                        "type": "string",
                        "default": ""
                    }
                ]
            }
        },
        {
            "name": "searchIdentifiers",
            "type": [
                "null",
                {
                    "type": "record",
                    "name": "SearchIdentifiers",
                    "namespace": "storyblocks.search.records",
                    "fields": [
                        {
                            "name": "searchId",
                            "type": "string",
                            "doc": "f.k.a. SSLID"
                        },
                        {
                            "name": "searchPageId",
                            "type": "string",
                            "doc": "f.k.a. SGUID"
                        },
                        {
                            "name": "lastSearchId",
                            "type": [
                                "null",
                                "string"
                            ],
                            "doc": "Previous searchId",
                            "default": null
                        }
                    ]
                }
            ],
            "default": null
        },
        {
            "name": "clickOrigin",
            "type": "string",
            "default": ""
        },
        {
            "name": "statusCode",
            "type": "int"
        },
        {
            "name": "ipAddress",
            "type": "string",
            "default": ""
        },
        {
            "name": "geo",
            "type": [
                "null",
                {
                    "type": "record",
                    "name": "Geo",
                    "namespace": "storyblocks.common",
                    "fields": [
                        {
                            "name": "countryCode",
                            "type": "string"
                        },
                        {
                            "name": "country",
                            "type": "string"
                        },
                        {
                            "name": "continent",
                            "type": "string"
                        }
                    ]
                }
            ],
            "default": null
        }
    ]
}

    public schema(): object {
        return PageViewEvent.schema;
    }

    public subject(): string {
        return PageViewEvent.subject;
    }
}
